//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import { CuralateAuthTokenHeader } from '@constants/CuralateAuthTokenHeader';
import { getQuerySeparator } from '@utils/getQuerySeparator';

import {
  IBaseUrls,
  IBasicCuralateSession,
  ICuralateSession,
} from 'app/basic-session/ICuralateSession';
import { crl8, isAuthedSession } from 'app/curalate.globals';
import { getRequiredJwtToken } from 'app/initialization/amplify/auth';

export const sessionXhr = async (
  service: keyof IBaseUrls,
  endpoint: string,
  uploadProgress: (xhr: XMLHttpRequest, progress: number) => void,
  init: RequestInit,
): Promise<XMLHttpRequest> => {
  const url = `${crl8.session?.baseUrls[service]}${endpoint}`;
  const session = crl8.session as
    | ICuralateSession
    | IBasicCuralateSession
    | undefined;

  const headers: Record<string, string> =
    (init.headers && (init.headers as Record<string, string>)) ?? {};

  init.credentials ??= 'same-origin';

  headers[CuralateAuthTokenHeader] = await getRequiredJwtToken();

  const safeClientId =
    (isAuthedSession(session) &&
      `${getQuerySeparator(url)}clientId=${session.client.id}`) ||
    '';

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(init.method ?? 'POST', `${url}${safeClientId}`);

    for (const key in headers) {
      const value = headers[key];

      if (value) {
        xhr.setRequestHeader(key, value);
      }
    }

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        uploadProgress(xhr, event.loaded / event.total);
      }
    });

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        // Mark as a successful response if the status code is 2xx
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(xhr);
        } else {
          reject(xhr);
        }
      }
    };
    xhr.send(init.body);
  });
};
